import React, { useState, useEffect } from 'react';
import { Layout, Menu, ConfigProvider, Grid } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, hasPermission } from '../contexts/AuthContext';
import {
  HomeOutlined,
  AppstoreOutlined,
  ShoppingCartOutlined,
  ImportOutlined,
  TeamOutlined,
  UserOutlined,
  ScheduleOutlined,
  BarChartOutlined,
  SettingOutlined,
  WalletOutlined,
  BankOutlined,
  SwapOutlined,
  TransactionOutlined,
  GiftOutlined,
  FireOutlined,
  InboxOutlined,
  BuildOutlined,
  IdcardOutlined,
  CalculatorOutlined,
  HistoryOutlined,
  TagsOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';

const { Sider } = Layout;

const allMenuItems = [
  { key: '/', icon: <HomeOutlined />, label: 'Anasayfa' },
  {
    key: 'mutfak',
    icon: <FireOutlined />,
    label: 'Mutfak Islemleri',
    children: [
      { key: '/malzemeler', icon: <InboxOutlined />, label: 'Malzemeler' },
      { key: '/urunler', icon: <AppstoreOutlined />, label: 'Urunler' },
      { key: '/uretimler', icon: <BuildOutlined />, label: 'Uretim' },
      { key: '/paketleme', icon: <GiftOutlined />, label: 'Paketleme' },
    ],
  },
  {
    key: 'finansal',
    icon: <TransactionOutlined />,
    label: 'Finansal Islemler',
    children: [
      { key: '/satislar', icon: <ShoppingCartOutlined />, label: 'Satislar' },
      { key: '/alislar', icon: <ImportOutlined />, label: 'Alislar' },
      { key: '/odemeler', icon: <WalletOutlined />, label: 'Odemeler' },
      { key: '/tahsilatlar', icon: <BankOutlined />, label: 'Tahsilatlar' },
      { key: '/virman', icon: <SwapOutlined />, label: 'Virman' },
      { key: '/cariler', icon: <IdcardOutlined />, label: 'Cariler' },
      { key: '/musteriler', icon: <UserOutlined />, label: 'Musteriler' },
      { key: '/kasalar', icon: <CalculatorOutlined />, label: 'Kasalar' },
    ],
  },
  // { key: '/ajanda', icon: <CalendarOutlined />, label: 'Ajanda' }, // ileride kullanıcı notları için yeniden açılabilir
  { type: 'divider' },
  { key: '/kullanicilar', icon: <TeamOutlined />, label: 'Kullanicilar' },
  { key: '/satis-takvimi', icon: <ScheduleOutlined />, label: 'Satis Takvimi' },
  { key: '/raporlama', icon: <BarChartOutlined />, label: 'Raporlama' },
  { key: '/log-kayitlari', icon: <HistoryOutlined />, label: 'Log Kayitlari' },
  { type: 'divider' },
  {
    key: 'ayarlar',
    icon: <SettingOutlined />,
    label: 'Ayarlar',
    children: [
      { key: '/ayarlar/liste-fiyatlari', icon: <TagsOutlined />, label: 'Liste Fiyatlari' },
      { key: '/ayarlar/gelir-tipleri', icon: <DatabaseOutlined />, label: 'Seçenek Ayarları' },
    ],
  },
];

function filterMenuItems(items, user, rolePermissions) {
  return items
    .map((item) => {
      if (item.type === 'divider') return item;
      if (item.children) {
        const filtered = item.children.filter((child) => hasPermission(user, child.key, rolePermissions));
        if (filtered.length === 0) return null;
        return { ...item, children: filtered };
      }
      if (item.key === '/') return item;
      return hasPermission(user, item.key, rolePermissions) ? item : null;
    })
    .filter(Boolean);
}

function Sidebar({ collapsed = false, onMenuClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, rolePermissions } = useAuth();
  const screens = Grid.useBreakpoint();
  // Mobil: <768 → 40px; Tablet: 768-991 → 56px; Desktop: ≥992 → 80px
  const collapsedWidth = !screens.md ? 40 : screens.lg ? 80 : 56;

  const menuItems = filterMenuItems(allMenuItems, user, rolePermissions);

  const mutfakPaths = ['/malzemeler', '/urunler', '/uretimler', '/paketleme'];
  const finansalPaths = ['/satislar', '/alislar', '/odemeler', '/tahsilatlar', '/virman', '/cariler', '/musteriler', '/kasalar'];

  // Submenu açık/kapalı durumunu kontrollü tutuyoruz — sidebar daralırsa
  // içindeki açık submenu de sıfırlansın (yoksa visual'de açık kalabiliyor).
  const [openKeys, setOpenKeys] = useState([]);

  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
    } else {
      const keys = [];
      if (location.pathname.startsWith('/ayarlar')) keys.push('ayarlar');
      if (finansalPaths.includes(location.pathname)) keys.push('finansal');
      if (mutfakPaths.includes(location.pathname)) keys.push('mutfak');
      setOpenKeys(keys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, location.pathname]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={220}
      collapsedWidth={collapsedWidth}
      style={{
        background: '#fff',
        borderRight: '1px solid #f0f0f0',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div style={{
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '2px solid #FF6B00',
        margin: collapsed ? 0 : '0 16px',
        overflow: 'hidden',
      }}>
        <span style={{
          fontSize: collapsed ? 24 : 28,
          fontWeight: 900,
          letterSpacing: collapsed ? 0 : 4,
          color: '#FF6B00',
          fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
          transition: 'all 0.2s',
          whiteSpace: 'nowrap',
        }}>
          {collapsed ? 'E' : 'EVDEYE'}
        </span>
      </div>
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              itemSelectedBg: '#FFF3E6',
              itemSelectedColor: '#FF6B00',
              itemHoverBg: '#FFF8F0',
              itemHoverColor: '#FF6B00',
              iconSize: 18,
              itemHeight: 44,
            },
          },
        }}
      >
        <Menu
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems}
          onClick={({ key }) => {
            if (key === 'ayarlar' || key === 'finansal' || key === 'mutfak') return;
            navigate(key);
            setOpenKeys([]); // alt menüleri kapat
            if (onMenuClick) onMenuClick();
          }}
          style={{ border: 'none', marginTop: 8 }}
        />
      </ConfigProvider>
    </Sider>
  );
}

export default Sidebar;
